import React, { useEffect } from 'react';
import { STITCH_TIERS } from '../data/stitchData';

export default function TierDetailModal({ tierId, onClose, onScheduleClick }) {
  useEffect(() => {
    if (!tierId) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [tierId, onClose]);

  if (!tierId) return null;
  const tier = STITCH_TIERS[tierId];
  if (!tier) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 lg:p-8">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-gray-950/70 backdrop-blur-md transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal Card */}
      <div className="relative z-10 w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white dark:bg-clinical-slate-900 rounded-3xl border border-glass-border-light dark:border-gray-800 shadow-[0_32px_72px_-12px_rgba(0,0,0,0.5)] flex flex-col">
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close modal"
          className="absolute top-5 right-5 z-20 w-10 h-10 rounded-full bg-gray-100 dark:bg-clinical-slate-800 hover:bg-primary hover:text-white flex items-center justify-center text-gray-700 dark:text-gray-200 transition-all shadow-sm"
        >
          <span className="material-symbols-outlined text-[20px]">close</span>
        </button>

        {/* Modal Header Banner */}
        <div className="relative p-6 sm:p-10 border-b border-gray-100 dark:border-gray-800 bg-gradient-to-br from-blue-50/70 via-white to-blue-50/30 dark:from-clinical-slate-800/90 dark:via-clinical-slate-900 dark:to-clinical-slate-800/60">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pr-10">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-ice-cyan dark:bg-clinical-slate-800 text-primary dark:text-primary-fixed flex items-center justify-center shrink-0 shadow-sm">
                <span className="material-symbols-outlined text-[32px]">{tier.icon}</span>
              </div>
              <div className="space-y-1">
                <span className="text-xs uppercase tracking-wider text-primary dark:text-primary-fixed font-bold">
                  {tier.badge}
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
                  {tier.title}
                </h2>
                <p className="text-sm font-semibold text-accent-teal">
                  {tier.subtitle}
                </p>
              </div>
            </div>

            <div className="self-start sm:self-center shrink-0 pt-2 sm:pt-0">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  if (onScheduleClick) onScheduleClick(tier.title);
                }}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary-container shadow-md hover:scale-105 active:scale-95 transition-all"
              >
                <span>Schedule Tier Consultation</span>
                <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
              </button>
            </div>
          </div>

          <p className="text-sm sm:text-base text-gray-600 dark:text-slate-300 mt-4 leading-relaxed max-w-3xl">
            {tier.overview}
          </p>
        </div>

        {/* Modal Content Body */}
        <div className="p-6 sm:p-10 space-y-8">
          {/* Mapped Practice Protocols & Services */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <span className="material-symbols-outlined text-primary text-[22px]">hub</span>
                <span>Mapped Practice Protocols &amp; Services</span>
              </h3>
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Clinical Coverage
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {tier.services.map((srv, sIdx) => (
                <div
                  key={sIdx}
                  className="p-5 rounded-2xl bg-surface-container-low/70 dark:bg-clinical-slate-800/60 border border-gray-150 dark:border-gray-700 space-y-2 hover:border-primary/40 transition-colors"
                >
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="text-base font-bold text-gray-900 dark:text-white">{srv.title}</h4>
                    <span className="px-3 py-1 rounded-full text-[11px] font-semibold bg-blue-50 dark:bg-clinical-slate-800 text-primary dark:text-blue-300 border border-blue-200/80 dark:border-blue-900/60 shrink-0">
                      {srv.tag}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-300 leading-relaxed">{srv.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Tier Expected ROI Highlights */}
          <div className="p-6 rounded-2xl bg-surface-container-low dark:bg-clinical-slate-800/80 border border-gray-200 dark:border-gray-700 space-y-4">
            <h4 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <span className="material-symbols-outlined text-accent-teal text-[22px]">trending_up</span>
              <span>Expected Strategic Impact &amp; Clinical Governance ROI</span>
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
              {tier.metrics.map((m, mIdx) => (
                <div
                  key={mIdx}
                  className="p-4 rounded-xl bg-white dark:bg-clinical-slate-900 border border-gray-150 dark:border-gray-700 flex flex-col items-center text-center shadow-xs"
                >
                  <span className="text-xl sm:text-2xl font-extrabold text-primary dark:text-blue-300">{m.val}</span>
                  <span className="text-xs font-semibold text-gray-900 dark:text-white mt-1">{m.label}</span>
                  <span className="text-[11px] text-gray-500 dark:text-slate-400">{m.sub}</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 dark:text-gray-500 text-center italic pt-1">
              *Strategic impact benchmarks derived from peer-review historical casework; actual scopes calibrated per institution.
            </p>
          </div>

          {/* Action Footer */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2 border-t border-gray-150 dark:border-gray-800">
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span className="material-symbols-outlined text-accent-teal text-[18px]">verified_user</span>
              <span>Operating strictly under statutory healthcare peer-review protections and confidentiality covenants.</span>
            </div>

            <button
              type="button"
              onClick={() => {
                onClose();
                if (onScheduleClick) onScheduleClick(tier.title);
              }}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-primary hover:bg-primary-container text-white text-sm font-semibold shadow-md hover:scale-105 active:scale-95 transition-all"
            >
              <span>Initiate Tier Briefing</span>
              <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
